import { CardLayout, MTable } from "../v3";
import { subcontractorReportColumns } from "../../pages/report/constants";
import { AnalyticsService } from "../../services";
import { useReportPdfDownload } from "../../hooks/useReportPdfDownload";
import { sumReportField, withReportRowMeta, toSubcontractorDetailedTableRow } from "../../utils/report/reportRows.util";
import type {
  ReportType,
  SubcontractorReportDetailedRow,
  SubcontractorReportQuery,
  SubcontractorReportResponse,
  SubcontractorReportSummaryRow,
} from "../../services/analytics.types";

interface PaginationState {
  page: number;
  pageSize: number;
}

interface SubcontractorReportResultsProps {
  reportType: ReportType;
  data: SubcontractorReportResponse;
  loading?: boolean;
  paginationState: PaginationState;
  handlePaginationChange?: (model: PaginationState) => void;
  reportFilters: SubcontractorReportQuery;
  showExportButton?: boolean;
}

export const SubcontractorReportResults = ({
  reportType,
  data,
  loading = false,
  paginationState,
  handlePaginationChange = () => {},
  reportFilters,
  showExportButton = false,
}: SubcontractorReportResultsProps) => {
  const { isPdfGenerating, pdfSuccess, prevFilters, handleDownloadPdfClick } = useReportPdfDownload({
    filters: reportFilters,
    download: AnalyticsService.downloadSubcontractorReportPdf,
    fileNamePrefix: `subcontractor-report-${reportType}`,
  });

  const withRowMeta = withReportRowMeta(paginationState.page * paginationState.pageSize);

  let tableRows: object[];
  if (reportType === "summary") {
    const rows = (data.rows as SubcontractorReportSummaryRow[]).map(withRowMeta);
    const subtotal = {
      id: "subtotal",
      label: "Subtotal",
      totalOpeningBalance: sumReportField(rows, "openingBalance"),
      totalContractValueSum: sumReportField(rows, "totalContractValue"),
      totalPaidSum: sumReportField(rows, "totalPaid"),
      totalClosingBalance: sumReportField(rows, "closingBalance"),
    };
    tableRows = rows.length ? [...rows, subtotal] : rows;
  } else {
    const rows = (data.rows as SubcontractorReportDetailedRow[]).map(toSubcontractorDetailedTableRow).map(withRowMeta);
    const subtotal = {
      id: "subtotal",
      label: "Subtotal",
      totalContract: sumReportField(rows, "contractAmount"),
      totalPayment: sumReportField(rows, "paymentAmount"),
    };
    tableRows = rows.length ? [...rows, subtotal] : rows;
  }

  return (
    <CardLayout>
      <MTable
        // Export data
        showExportButton={showExportButton}
        isPdfGenerating={isPdfGenerating}
        pdfSuccess={pdfSuccess}
        currentFilters={reportFilters}
        prevFilters={prevFilters}
        onExportPdfClick={handleDownloadPdfClick}
        // Table data
        columnHeaders={subcontractorReportColumns[reportType]}
        tableRows={tableRows}
        rowCount={data.count}
        loading={loading}
        paginationModel={paginationState}
        onPaginationChange={handlePaginationChange}
      />
    </CardLayout>
  );
};
